import { computed, onScopeDispose, reactive, ref, unref } from "vue";
import type { Ref } from "vue";

import { masterPortfolioApi } from "@/entities/master-portfolio";
import type { MasterPortfolioPhoto } from "@/entities/master-portfolio";
import { masterServiceApi } from "@/entities/master-service";
import type { MasterServiceConfig } from "@/entities/master-service";
import { isAppError } from "@/shared/api";
import type { AppError } from "@/shared/api";
import { i18n } from "@/shared/i18n";
import { toast } from "@/shared/lib";

import { mapPortfolioError } from "./mapPortfolioError";

function tr(key: string, params?: Record<string, unknown>): string {
  return params !== undefined ? i18n.global.t(key, params) : i18n.global.t(key);
}

export interface UseMasterPortfolioOptions {
  onNotFound: () => void;
}

export function useMasterPortfolio(
  masterId: string | Ref<string>,
  opts: UseMasterPortfolioOptions,
) {
  const listStatus = ref<"loading" | "ready" | "error">("loading");
  const photos = ref<MasterPortfolioPhoto[]>([]);
  const services = ref<MasterServiceConfig[]>([]);
  const reorderInFlight = ref(false);
  const politeMessage = ref("");

  const upload = reactive({
    open: false,
    status: "idle" as "idle" | "uploading" | "error",
    progress: 0 as number | "indeterminate",
    file: null as File | null,
    previewUrl: null as string | null,
    serviceId: null as string | null,
    fileErrorKey: null as string | null,
    serviceErrorKey: null as string | null,
  });

  const remove = reactive({
    state: "idle" as "idle" | "confirming" | "removing",
    targetId: null as string | null,
    errorKey: null as string | null,
  });

  let canceled = false;
  let uploadController: AbortController | null = null;

  // -----------------------------------------------------------------------
  // Derived
  // -----------------------------------------------------------------------

  const isFull = computed(() => photos.value.length >= 20);

  const serviceOptions = computed(() =>
    services.value.map((s) => ({ value: s.serviceId, label: s.name })),
  );

  function resolveServiceName(serviceId: string | null): string | undefined {
    if (!serviceId) return undefined;
    return services.value.find((s) => s.serviceId === serviceId)?.name;
  }

  const canReorder = computed(() => photos.value.length > 1 && !reorderInFlight.value);

  // -----------------------------------------------------------------------
  // load
  // -----------------------------------------------------------------------

  async function load(): Promise<void> {
    canceled = false;
    listStatus.value = "loading";

    try {
      const id = unref(masterId);
      const [photoList, serviceList] = await Promise.all([
        masterPortfolioApi.listPortfolioPhotos(id),
        masterServiceApi.listMasterServices(id),
      ]);

      if (canceled) return;

      photos.value = photoList;
      services.value = serviceList;
      listStatus.value = "ready";
    } catch (err) {
      if (canceled) return;

      const appErr: AppError = isAppError(err) ? err : { kind: "server", retriable: true };
      const mapped = mapPortfolioError(appErr);

      if (mapped.surface === "notFound") {
        opts.onNotFound();
        return;
      }
      listStatus.value = "error";
    }
  }

  // -----------------------------------------------------------------------
  // Upload actions
  // -----------------------------------------------------------------------

  function openUpload(): void {
    upload.open = true;
    upload.status = "idle";
    upload.progress = 0;
    upload.fileErrorKey = null;
    upload.serviceErrorKey = null;
    upload.serviceId = null;
  }

  function closeUpload(): void {
    upload.open = false;
    if (upload.previewUrl) {
      URL.revokeObjectURL(upload.previewUrl);
      upload.previewUrl = null;
    }
    upload.file = null;
    upload.status = "idle";
    upload.progress = 0;
    upload.fileErrorKey = null;
    upload.serviceErrorKey = null;
    upload.serviceId = null;
  }

  function selectFile(file: File): void {
    if (upload.previewUrl) {
      URL.revokeObjectURL(upload.previewUrl);
    }
    upload.file = file;
    upload.previewUrl = URL.createObjectURL(file);
    upload.fileErrorKey = null;
  }

  function setUploadService(serviceId: string | null): void {
    upload.serviceId = serviceId;
  }

  async function confirmUpload(): Promise<void> {
    if (upload.status === "uploading" || !upload.file) return;

    uploadController = new AbortController();
    upload.status = "uploading";
    upload.progress = 0;
    upload.fileErrorKey = null;
    upload.serviceErrorKey = null;

    try {
      const photo = await masterPortfolioApi.uploadPortfolioPhoto(
        unref(masterId),
        upload.file,
        {
          serviceId: upload.serviceId || undefined,
          onProgress: (pct) => {
            upload.progress = pct;
          },
          signal: uploadController.signal,
        },
      );

      if (canceled) return;

      photos.value.push(photo);
      politeMessage.value = tr("masters.portfolio.a11y.photoAdded");
      toast.success(tr("masters.portfolio.a11y.photoAdded"));
      closeUpload();
    } catch (err) {
      if (canceled) return;

      const appErr: AppError = isAppError(err) ? err : { kind: "server", retriable: true };

      // Canceled upload is not an error
      if (appErr.kind === "unknown") {
        return;
      }

      const mapped = mapPortfolioError(appErr);
      upload.status = "error";

      switch (mapped.surface) {
        case "inline":
          upload.fileErrorKey = mapped.i18nKey ?? null;
          break;
        case "field":
          upload.serviceErrorKey = mapped.i18nKey ?? null;
          break;
        case "notFound":
          opts.onNotFound();
          break;
        case "toast":
          toast.error(tr(mapped.i18nKey ?? "errors.unknown"));
          break;
        default:
          upload.fileErrorKey = mapped.i18nKey ?? null;
      }
    }
  }

  function cancelUpload(): void {
    uploadController?.abort();
    closeUpload();
  }

  // -----------------------------------------------------------------------
  // Remove actions
  // -----------------------------------------------------------------------

  function requestRemove(photoId: string): void {
    remove.state = "confirming";
    remove.targetId = photoId;
    remove.errorKey = null;
  }

  function cancelRemove(): void {
    remove.state = "idle";
    remove.targetId = null;
  }

  async function confirmRemove(): Promise<void> {
    if (remove.state === "removing" || !remove.targetId) return;

    remove.state = "removing";

    try {
      await masterPortfolioApi.deletePortfolioPhoto(unref(masterId), remove.targetId);

      if (canceled) return;

      removePhotoAndRecompact(remove.targetId);
      remove.state = "idle";
      remove.targetId = null;
      politeMessage.value = tr("masters.portfolio.a11y.photoDeleted");
      toast.success(tr("masters.portfolio.a11y.photoDeleted"));
    } catch (err) {
      if (canceled) return;

      const appErr: AppError = isAppError(err) ? err : { kind: "server", retriable: true };
      const mapped = mapPortfolioError(appErr);

      if (mapped.surface === "silent") {
        removePhotoAndRecompact(remove.targetId!);
        remove.state = "idle";
        remove.targetId = null;
        return;
      }

      if (mapped.surface === "toast") {
        toast.error(tr(mapped.i18nKey ?? "errors.unknown"));
        remove.state = "idle";
        return;
      }
      remove.errorKey = mapped.i18nKey ?? null;
      remove.state = "confirming";
    }
  }

  function removePhotoAndRecompact(photoId: string): void {
    photos.value = photos.value
      .filter((p) => p.id !== photoId)
      .map((p, i) => ({ ...p, position: i }));
  }

  // -----------------------------------------------------------------------
  // Reorder actions
  // -----------------------------------------------------------------------

  async function moveBack(id: string): Promise<void> {
    if (!canReorder.value) return;
    const idx = photos.value.findIndex((p) => p.id === id);
    if (idx <= 0) return;

    const snapshot = [...photos.value];
    const swapped = [...photos.value];
    [swapped[idx - 1], swapped[idx]] = [swapped[idx], swapped[idx - 1]];
    photos.value = swapped.map((p, i) => ({ ...p, position: i }));

    politeMessage.value = tr("masters.portfolio.a11y.photoMoved", {
      position: idx,
      total: photos.value.length,
    });

    reorderInFlight.value = true;
    try {
      const result = await masterPortfolioApi.reorderPortfolioPhotos(
        unref(masterId),
        photos.value.map((p) => ({ id: p.id, position: p.position })),
      );
      if (canceled) return;
      photos.value = result;
    } catch {
      if (canceled) return;
      photos.value = snapshot;
      toast.error(tr("masters.portfolio.errors.reorderFailed"));
      void load();
    } finally {
      reorderInFlight.value = false;
    }
  }

  async function moveForward(id: string): Promise<void> {
    if (!canReorder.value) return;
    const idx = photos.value.findIndex((p) => p.id === id);
    if (idx < 0 || idx >= photos.value.length - 1) return;

    const snapshot = [...photos.value];
    const swapped = [...photos.value];
    [swapped[idx], swapped[idx + 1]] = [swapped[idx + 1], swapped[idx]];
    photos.value = swapped.map((p, i) => ({ ...p, position: i }));

    politeMessage.value = tr("masters.portfolio.a11y.photoMoved", {
      position: idx + 2,
      total: photos.value.length,
    });

    reorderInFlight.value = true;
    try {
      const result = await masterPortfolioApi.reorderPortfolioPhotos(
        unref(masterId),
        photos.value.map((p) => ({ id: p.id, position: p.position })),
      );
      if (canceled) return;
      photos.value = result;
    } catch {
      if (canceled) return;
      photos.value = snapshot;
      toast.error(tr("masters.portfolio.errors.reorderFailed"));
      void load();
    } finally {
      reorderInFlight.value = false;
    }
  }

  // -----------------------------------------------------------------------
  // Cleanup
  // -----------------------------------------------------------------------

  onScopeDispose(() => {
    canceled = true;
    if (uploadController) {
      uploadController.abort();
    }
    if (upload.previewUrl) {
      URL.revokeObjectURL(upload.previewUrl);
    }
  });

  return {
    listStatus,
    photos,
    services,
    isFull,
    serviceOptions,
    resolveServiceName,
    canReorder,
    upload,
    remove,
    reorderInFlight,
    politeMessage,
    load,
    openUpload,
    closeUpload,
    selectFile,
    setUploadService,
    confirmUpload,
    cancelUpload,
    requestRemove,
    cancelRemove,
    confirmRemove,
    moveBack,
    moveForward,
  };
}
